// Step 4: Payment Form Component with Stripe Checkout

import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowPathIcon, CreditCardIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { hiringService } from '@/services/hiringService';
import { hiringProgressService } from '@/services/hiringProgressService';
// Dynamic import para PDF generator (pesado, cargar bajo demanda)
import type { HiringDetails } from '@/types/hiring';

interface PaymentFormProps {
  hiringCode: string;
  amount: number;
  currency: string;
  serviceName: string;
  hiringDetails?: HiringDetails;
  onSuccess: () => void;
  onBack: () => void;
}

export function PaymentForm(props: PaymentFormProps) {
  const { t } = useTranslation('payment');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [checkoutInfo, setCheckoutInfo] = useState<{ payment_type?: 'first' | 'subscription'; installments?: number; amount?: number; total_amount?: number } | null>(null);
  
  // Si el código viene con manual_payment_confirmed del backend, activar modo manual automáticamente
  const adminManualPayment = props.hiringDetails?.manual_payment_confirmed || false;
  const adminManualNote = props.hiringDetails?.manual_payment_note || '';
  
  console.log('💰 PaymentForm - hiringDetails recibidos:', props.hiringDetails);
  console.log('💰 PaymentForm - payment_type en hiringDetails:', props.hiringDetails?.payment_type);
  console.log('💰 PaymentForm - amount en hiringDetails:', props.hiringDetails?.amount);
  console.log('💰 PaymentForm - grade en hiringDetails:', props.hiringDetails?.grade);
  console.log('💰 PaymentForm - manual_payment_confirmed:', adminManualPayment);
  console.log('💰 PaymentForm - manual_payment_note:', adminManualNote);
  
  // Estado para pago manual solo si el admin lo confirmó (solo para renderizado, no editable)
  const manualPaymentMode = adminManualPayment;

  // Obtener el monto del primer pago del backend (en centavos) o calcularlo como fallback
  const getFirstPaymentAmount = (): number => {
    const paymentType = getPaymentType();
    const totalAmount = checkoutInfo?.total_amount || props.hiringDetails?.amount || 0;
    
    // Si es suscripción, el primer pago es el 10% del total (pago mensual)
    if (paymentType === 'subscription' && totalAmount > 0) {
      // Prioridad 1: Usar amount del checkout response (que debería ser el pago mensual)
      if (checkoutInfo?.amount !== undefined && checkoutInfo.amount > 0) {
        return checkoutInfo.amount;
      }
      // Prioridad 2: Usar first_payment_amount del backend si es válido
      if (props.hiringDetails?.first_payment_amount !== undefined && props.hiringDetails.first_payment_amount > 0) {
        return props.hiringDetails.first_payment_amount;
      }
      // Fallback: Calcular como 10% del total (10 pagos mensuales)
      return Math.round(totalAmount / 10);
    }
    
    // Para pago único 100% (one_time_full), cobrar el total en un solo cargo
    if (paymentType === 'one_time_full') {
      if (props.hiringDetails?.first_payment_amount !== undefined && props.hiringDetails.first_payment_amount > 0) {
        return props.hiringDetails.first_payment_amount;
      }
      if (checkoutInfo?.amount !== undefined && checkoutInfo.amount > 0) {
        return checkoutInfo.amount;
      }
      return totalAmount;
    }

    // Para pago único en 2 plazos (one_time), usar 50% del total
    // Prioridad 1: Usar first_payment_amount del backend
    if (props.hiringDetails?.first_payment_amount !== undefined) {
      return props.hiringDetails.first_payment_amount;
    }
    // Prioridad 2: Usar amount del checkout response
    if (checkoutInfo?.amount !== undefined) {
      return checkoutInfo.amount;
    }
    // Fallback: Calcular según el grado (solo si no hay datos del backend)
    const grade = props.hiringDetails?.grade;
    if (grade === 'T') {
      return 50; // 0.50 EUR en centavos
    } else if (grade === 'C') {
      return 30000; // 300 EUR en centavos
    } else {
      // Grados A y B
      return 20000; // 200 EUR en centavos
    }
  };

  // Obtener el tipo de pago del backend
  const getPaymentType = (): 'one_time' | 'subscription' | 'one_time_full' => {
    const totalAmount = checkoutInfo?.total_amount || props.hiringDetails?.amount || 0;
    const firstPaymentAmount = props.hiringDetails?.first_payment_amount;
    const hiringPaymentType = props.hiringDetails?.payment_type;
    const checkoutPaymentType = checkoutInfo?.payment_type;
    
    console.log('🔍 PaymentForm - Detecting payment type:', {
      hiringPaymentType,
      checkoutPaymentType,
      totalAmount,
      firstPaymentAmount
    });
    
    // Prioridad 1: Respetar explícitamente one_time_full del backend
    if (hiringPaymentType === 'one_time_full') {
      console.log('✅ PaymentForm - Usando payment_type de hiringDetails: one_time_full');
      return 'one_time_full';
    }

    // Prioridad 2: Detectar automáticamente basándose en montos (más confiable)
    const totalEuros = totalAmount > 1000 ? totalAmount / 100 : totalAmount;
    if (totalEuros === 480 || totalEuros === 680 || totalAmount === 48000 || totalAmount === 68000) {
      console.log('✅ PaymentForm - Detectado como subscription: total = 480€ o 680€');
      return 'subscription';
    }

    const tolerance = totalAmount > 1000 ? 100 : 1;
    if (totalAmount > 0 && firstPaymentAmount && Math.abs(firstPaymentAmount - (totalAmount / 10)) < tolerance) {
      console.log('✅ PaymentForm - Detectado como subscription: primer pago = 10% del total');
      return 'subscription';
    }
    
    // Prioridad 3: Usar payment_type de hiringDetails (solo si no contradice los montos)
    if (hiringPaymentType === 'subscription') {
      console.log('✅ PaymentForm - Usando payment_type de hiringDetails: subscription');
      return 'subscription';
    }
    if (hiringPaymentType === 'one_time') {
      console.log('✅ PaymentForm - Usando payment_type de hiringDetails: one_time');
      return 'one_time';
    }
    
    // Prioridad 4: Inferir del checkout response
    if (checkoutPaymentType === 'subscription') {
      console.log('✅ PaymentForm - Usando payment_type de checkoutInfo: subscription');
      return 'subscription';
    }
    
    // 'first' significa pago único
    if (checkoutPaymentType === 'first') {
      console.log('✅ PaymentForm - Usando payment_type de checkoutInfo: one_time (first)');
      return 'one_time';
    }
    
    // Default: pago único
    console.log('⚠️ PaymentForm - No se detectó payment_type, usando default: one_time');
    return 'one_time';
  };

  // API/checkout devuelven montos en EUR; tolerar legacy en centavos si > 1000
  const toEuros = (raw: number): number => {
    if (raw > 1000) return raw / 100;
    return raw;
  };

  // Formatear monto para mostrar: valor ya en EUR
  const formatAmount = (amountInEuros: number): string => {
    return amountInEuros.toFixed(2);
  };

  // Helper para renderizar información de pago según el tipo
  const renderPaymentInfo = () => {
    const paymentType = getPaymentType();
    const totalAmount = checkoutInfo?.total_amount || props.hiringDetails?.amount || 0;
    const installments = checkoutInfo?.installments || (paymentType === 'subscription' ? 10 : 2);
    const isSubscription = paymentType === 'subscription';
    const isOneTimeFull = paymentType === 'one_time_full';
    
    console.log('🎨 renderPaymentInfo - paymentType:', paymentType);
    console.log('🎨 renderPaymentInfo - isSubscription:', isSubscription);
    console.log('🎨 renderPaymentInfo - totalAmount:', totalAmount);
    console.log('🎨 renderPaymentInfo - installments:', installments);

    if (isSubscription) {
      const monthlyPaymentRaw = checkoutInfo?.amount || (totalAmount > 0 ? Math.round(totalAmount / 10) : 0);
      const monthlyEuros = toEuros(monthlyPaymentRaw);
      const totalEuros = toEuros(totalAmount);
      return (
        <>
          <p><strong>{t('info.service')}</strong> {props.serviceName}</p>
          <p><strong>{t('info.type')}</strong> <span className="font-semibold text-primary">{t('info.subscriptionType')}</span></p>
          <p><strong>{t('info.firstPayment')}</strong> €{formatAmount(monthlyEuros)}</p>
          <p><strong>{t('info.monthlyPayments')}</strong> {t('info.monthlyPaymentsDesc', { amount: formatAmount(monthlyEuros) })}</p>
          <p><strong>{t('info.totalService')}</strong> €{formatAmount(totalEuros)}</p>
          <p className="text-xs text-gray-600 mt-2 italic">
            {t('info.subscriptionNote')}
          </p>
        </>
      );
    } else if (isOneTimeFull) {
      const singlePaymentRaw = getFirstPaymentAmount();
      const singlePaymentEuros = toEuros(singlePaymentRaw);
      const totalEuros = toEuros(totalAmount);
      return (
        <>
          <p><strong>{t('info.service')}</strong> {props.serviceName}</p>
          <p><strong>{t('info.type')}</strong> <span className="font-semibold text-primary">{t('info.oneTimeFullType')}</span></p>
          <p><strong>{t('info.oneTimeFullPayment')}</strong> €{formatAmount(singlePaymentEuros)}</p>
          <p><strong>{t('info.totalService')}</strong> €{formatAmount(totalEuros)}</p>
          <p className="text-xs text-gray-600 mt-2 italic">
            {t('info.oneTimeFullNote')}
          </p>
        </>
      );
    } else {
      const firstPaymentRaw = getFirstPaymentAmount();
      const firstEuros = toEuros(firstPaymentRaw);
      const totalEuros = toEuros(totalAmount);
      return (
        <>
          <p><strong>{t('info.service')}</strong> {props.serviceName} {t('info.firstPaymentLabel')}</p>
          <p><strong>{t('info.type')}</strong> <span className="font-semibold text-primary">{t('info.twoInstallmentsType')}</span></p>
          <p><strong>{t('info.firstPayment50')}</strong> €{formatAmount(firstEuros)}</p>
          <p><strong>{t('info.totalService')}</strong> €{formatAmount(totalEuros)}</p>
          <p className="text-xs text-gray-600 mt-2 italic">
            {t('info.twoInstallmentsNote')}
          </p>
        </>
      );
    }
  };

  useEffect(() => {
    // Si el pago ya fue confirmado por el admin O si está en modo manual, no crear checkout
    if (manualPaymentMode || adminManualPayment) {
      setLoading(false);
      return;
    }
    const createCheckoutSession = async () => {
      try {
        console.log('🛒 Creando sesión de Stripe Checkout...');
        console.log('📋 hiringDetails antes de crear checkout:', props.hiringDetails);
        console.log('📋 payment_type esperado:', props.hiringDetails?.payment_type);
        
        const response = await hiringService.createCheckoutSession(props.hiringCode);
        
        console.log('✅ Checkout session creada:', response);
        console.log('📋 payment_type en response:', response.payment_type);
        console.log('📋 installments en response:', response.installments);
        console.log('📋 amount en response:', response.amount);
        console.log('📋 total_amount en response:', response.total_amount);
        
        setCheckoutUrl(response.checkout_url);
        // Guardar información del checkout para mostrar en la UI
        setCheckoutInfo({
          payment_type: response.payment_type,
          installments: response.installments,
          amount: response.amount,
          total_amount: response.total_amount,
        });
        void hiringProgressService.saveProgress(props.hiringCode, {
          step: 4,
          payment: {
            status: 'checkout_created',
            checkout_url: response.checkout_url,
            session_id: response.session_id,
            updated_at: new Date().toISOString(),
          },
        });
        
      } catch (err: any) {
        // Mostrar el mensaje de error del backend si está disponible
        const backendMessage = err.response?.data?.detail || err.response?.data?.message;
        
        if (backendMessage) {
          setError(t('serverError', { message: backendMessage }));
        } else if (props.hiringCode.startsWith('LIVE') && err.response?.status === 500) {
          setError(t('maintenanceError'));
        } else {
          setError(err.message || t('initError'));
        }
      } finally {
        setLoading(false);
      }
    };

    createCheckoutSession();
  }, [props.hiringCode, manualPaymentMode, adminManualPayment]);

  const handleStripeCheckout = () => {
    if (checkoutUrl) {
      void hiringProgressService.saveProgress(props.hiringCode, {
        step: 4,
        payment: {
          status: 'checkout_created',
          checkout_url: checkoutUrl,
          updated_at: new Date().toISOString(),
        },
      });
      console.log('🛒 Redirigiendo a Stripe Checkout...');
      window.location.href = checkoutUrl;
    }
  };

  const handleTestPayment = async () => {
    if (!props.hiringCode.startsWith('TEST')) {
      return;
    }

    console.log('🧪 Simulando pago TEST exitoso...');
    
    try {
      // Simular confirmación en el backend
      await hiringService.confirmPayment(props.hiringCode, 'pi_test_simulated');
      await hiringProgressService.saveProgress(props.hiringCode, {
        step: 5,
        payment: {
          status: 'paid',
          payment_intent_id: 'pi_test_simulated',
          updated_at: new Date().toISOString(),
        },
      });
      
      // Generar y enviar contrato definitivo
      if (props.hiringDetails) {
        const clientSignature = localStorage.getItem(`client_signature_${props.hiringCode}`);
        
        // Cargar anexos si existen
        let annexesToInclude: Array<{ title: string; content: string }> | undefined = undefined;
        try {
          const loadedAnnexes = await hiringService.getAnnexes(props.hiringCode);
          if (loadedAnnexes.length > 0) {
            annexesToInclude = loadedAnnexes.map(a => ({ title: a.title, content: a.content }));
            console.log('📎 Anexos cargados para PDF (test):', annexesToInclude.length);
          }
        } catch (annexError) {
          console.warn('No se pudieron cargar anexos:', annexError);
        }
        
        // Obtener firmas manuscritas con fallback al backend
        const handwrittenSignatures = await hiringProgressService.getHandwrittenSignatures(props.hiringCode);
        // Dynamic import para PDF generator y firma de Migro
        const { generateContractPDF } = await import('@/utils/contractPdfGenerator');
        const { getMigroSignatureFromStorage } = await import('@/config/migroSignatureStorage');
        const contractBlob = generateContractPDF(props.hiringDetails, {
          paymentIntentId: 'pi_test_simulated',
          stripeTransactionId: `test_${Date.now()}`,
          paymentDate: new Date().toISOString(),
          paymentMethod: 'Simulación TEST',
          clientSignature: clientSignature || undefined,
          handwrittenSignatures,
          migroSignatureImage: getMigroSignatureFromStorage() ?? undefined,
        }, false, annexesToInclude); // isDraft = false (contrato definitivo con firmas ePad y Migro)

        const formData = new FormData();
        formData.append('contract', contractBlob, `contrato_definitivo_${props.hiringCode}.pdf`);
        formData.append('hiring_code', props.hiringCode);
        formData.append('client_email', props.hiringDetails.client_email || '');
        formData.append('client_name', props.hiringDetails.client_name || '');
        formData.append('contract_type', 'final');
        formData.append('payment_intent_id', 'pi_test_simulated');
        formData.append('stripe_transaction_id', `test_${Date.now()}`);
        formData.append('payment_date', new Date().toISOString());
        formData.append('payment_method', 'Simulación TEST');

        await hiringService.uploadFinalContract(formData);
        console.log('✅ Contrato definitivo generado y enviado');
      }

      props.onSuccess();
      
    } catch (err: any) {
      console.error('❌ Error en pago TEST:', err);
      setError(t('testPaymentError'));
    }
  };

  const handleManualPaymentContinue = async () => {
    if (!props.hiringDetails) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Generando y subiendo contrato definitivo con pago manual...');
      
      // Obtener firma del cliente y firmas manuscritas con fallback al backend
      const clientSignature = localStorage.getItem(`client_signature_${props.hiringCode}`);
      const handwrittenSignatures = await hiringProgressService.getHandwrittenSignatures(props.hiringCode);

      // Cargar anexos si existen
      let annexesToInclude: Array<{ title: string; content: string }> | undefined = undefined;
      try {
        const loadedAnnexes = await hiringService.getAnnexes(props.hiringCode);
        if (loadedAnnexes.length > 0) {
          annexesToInclude = loadedAnnexes.map(a => ({ title: a.title, content: a.content }));
          console.log('📎 Anexos cargados para PDF (manual):', annexesToInclude.length);
        }
      } catch (annexError) {
        console.warn('No se pudieron cargar anexos:', annexError);
      }
      
      // Dynamic import para PDF generator y firma de Migro
      const { generateContractPDF } = await import('@/utils/contractPdfGenerator');
      const { getMigroSignatureFromStorage } = await import('@/config/migroSignatureStorage');
      // Generar PDF definitivo CON firmas ePad y bloque Migro (isDraft = false)
      const contractBlob = generateContractPDF(props.hiringDetails, {
        paymentIntentId: 'manual_payment',
        stripeTransactionId: `manual_${Date.now()}`,
        paymentDate: new Date().toISOString(),
        paymentMethod: props.hiringDetails.manual_payment_method || 'Pago manual',
        paymentNote: props.hiringDetails.manual_payment_note,
        clientSignature: clientSignature || undefined,
        handwrittenSignatures,
        migroSignatureImage: getMigroSignatureFromStorage() ?? undefined,
      }, false, annexesToInclude); // isDraft = false (contrato final SIN marca de agua)
      
      console.log('📄 Contrato definitivo generado con firma, tamaño:', contractBlob.size);
      
      // Preparar FormData para subir al backend
      const formData = new FormData();
      formData.append('contract', contractBlob, `contrato_definitivo_${props.hiringCode}.pdf`);
      formData.append('hiring_code', props.hiringCode);
      formData.append('client_email', props.hiringDetails.client_email || '');
      formData.append('client_name', props.hiringDetails.client_name || '');
      formData.append('contract_type', 'final');
      formData.append('payment_intent_id', 'manual_payment');
      formData.append('stripe_transaction_id', `manual_${Date.now()}`);
      formData.append('payment_date', new Date().toISOString());
      formData.append('payment_method', props.hiringDetails.manual_payment_method || 'Pago manual');
      if (props.hiringDetails.manual_payment_note) {
        formData.append('payment_note', props.hiringDetails.manual_payment_note);
      }
      
      // Subir contrato definitivo al backend
      await hiringService.uploadFinalContract(formData);
      console.log('✅ Contrato definitivo con pago manual subido exitosamente');
      await hiringProgressService.saveProgress(props.hiringCode, {
        step: 5,
        payment: {
          status: 'completed',
          payment_intent_id: 'manual_payment',
          updated_at: new Date().toISOString(),
        },
      });
      
      // Marcar como subido
      localStorage.setItem(`contract_uploaded_${props.hiringCode}`, 'true');
      
      // Continuar al siguiente paso
      props.onSuccess();
      
    } catch (err: any) {
      console.error('❌ Error subiendo contrato definitivo:', err);
      setError(t('processError'));
    } finally {
      setLoading(false);
    }
  };

  const renderManualCard = () => {
    // Si el pago manual fue confirmado por el admin, mostrarlo en modo lectura CON botón para continuar
    if (adminManualPayment) {
      return (
        <Card className="shadow-lg border-2 border-green-300 bg-green-50">
          <CardContent className="space-y-6 pt-6">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="text-green-600 mt-1 flex-shrink-0" width={28} height={28} />
              <div className="flex-1">
                <p className="text-xl font-semibold text-green-900 mb-2">{t('manualPaymentTitle')}</p>
                <p className="text-sm text-green-700">
                  {t('manualPaymentDesc')}
                </p>
                {adminManualNote && (
                  <div className="mt-4 bg-white border border-green-200 rounded-lg p-4">
                    <p className="text-xs font-semibold text-gray-700 mb-2">{t('manualPaymentMethod')}</p>
                    <p className="text-sm text-gray-900 font-medium">{adminManualNote}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-green-100 border border-green-300 rounded-lg p-4">
              <p className="text-sm text-green-900 mb-1">
                <strong>{t('noAdditionalPayment')}</strong>
              </p>
              <p className="text-xs text-green-700">
                {t('manualPaymentProcessed')}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
                <ExclamationCircleIcon width={20} height={20} />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                onClick={props.onBack}
                variant="outline"
                className="flex-1"
              >
                {t('common:buttons.back')}
              </Button>
              <Button
                onClick={handleManualPaymentContinue}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="mr-2 animate-spin" width={18} height={18} />
                    {t('common:buttons.processing')}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="mr-2" width={18} height={18} />
                    {t('continueToSign')}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Si no hay pago confirmado por admin, NO mostrar ninguna tarjeta editable
    // El cliente NUNCA debe tener la opción de marcar pago manual
    return null;
  };

  const manualCard = renderManualCard();

  let mainContent: ReactNode;

  if (loading) {
    mainContent = (
      <Card className="shadow-lg">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="animate-spin text-primary" width={32} height={32} />
            <span className="ml-3 text-lg">{t('loading')}</span>
          </div>
        </CardContent>
      </Card>
    );
  } else if (error) {
    mainContent = (
      <Card className="shadow-lg">
        <CardContent className="pt-6">
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
            <ExclamationCircleIcon width={20} height={20} />
            <p className="text-sm">{error}</p>
          </div>
          
          {props.hiringCode.startsWith('LIVE') && error.includes('mantenimiento') && (
            <div className="mt-4 bg-blue-50 border border-blue-200 text-blue-700 p-4 rounded-lg">
              <p className="text-sm mb-3">
                <strong>{t('maintenanceAlt')}</strong> {t('maintenanceAltDesc')}
              </p>
              <Button 
                onClick={async () => {
                  try {
                    await hiringService.confirmPayment(props.hiringCode, 'pi_live_simulated');
                    await hiringProgressService.saveProgress(props.hiringCode, {
                      step: 5,
                      payment: {
                        status: 'paid',
                        payment_intent_id: 'pi_live_simulated',
                        updated_at: new Date().toISOString(),
                      },
                    });
                    
                    if (props.hiringDetails) {
                      const clientSignature = localStorage.getItem(`client_signature_${props.hiringCode}`);
                      const handwrittenSignatures = await hiringProgressService.getHandwrittenSignatures(props.hiringCode);
                      // Cargar anexos si existen
                      let annexesToInclude: Array<{ title: string; content: string }> | undefined = undefined;
                      try {
                        const loadedAnnexes = await hiringService.getAnnexes(props.hiringCode);
                        if (loadedAnnexes.length > 0) {
                          annexesToInclude = loadedAnnexes.map(a => ({ title: a.title, content: a.content }));
                          console.log('📎 Anexos cargados para PDF (live):', annexesToInclude.length);
                        }
                      } catch (annexError) {
                        console.warn('No se pudieron cargar anexos:', annexError);
                      }
                      // Dynamic import para PDF generator y firma de Migro
                      const { generateContractPDF } = await import('@/utils/contractPdfGenerator');
                      const { getMigroSignatureFromStorage } = await import('@/config/migroSignatureStorage');
                      const contractBlob = generateContractPDF(props.hiringDetails, {
                        paymentIntentId: 'pi_live_simulated',
                        stripeTransactionId: `live_sim_${Date.now()}`,
                        paymentDate: new Date().toISOString(),
                        paymentMethod: 'Simulación LIVE (Mantenimiento)',
                        clientSignature: clientSignature || undefined,
                        handwrittenSignatures,
                        migroSignatureImage: getMigroSignatureFromStorage() ?? undefined,
                      }, false, annexesToInclude);

                      const formData = new FormData();
                      formData.append('contract', contractBlob, `contrato_definitivo_${props.hiringCode}.pdf`);
                      formData.append('hiring_code', props.hiringCode);
                      formData.append('client_email', props.hiringDetails.client_email || '');
                      formData.append('client_name', props.hiringDetails.client_name || '');
                      formData.append('contract_type', 'final');
                      formData.append('payment_intent_id', 'pi_live_simulated');
                      formData.append('stripe_transaction_id', `live_sim_${Date.now()}`);
                      formData.append('payment_date', new Date().toISOString());
                      formData.append('payment_method', 'Simulación LIVE (Mantenimiento)');

                      await hiringService.uploadFinalContract(formData);
                    }
                    
                    props.onSuccess();
                  } catch (err) {
                    console.error('Error en simulación LIVE:', err);
                    setError(t('simulationError'));
                  }
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t('maintenanceSimulate')}
              </Button>
            </div>
          )}
          
          <div className="mt-4 flex gap-4">
            <Button onClick={props.onBack} variant="outline" className="flex-1">
              {t('common:buttons.back')}
            </Button>
            <Button onClick={() => window.location.reload()} className="flex-1">
              {t('common:buttons.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  } else if (props.hiringCode.startsWith('TEST')) {
    mainContent = (
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10">
          <CardTitle className="text-2xl text-emphasis-900 flex items-center gap-2">
            <CreditCardIcon className="text-primary" width={28} height={28} />
            {t('testTitle')}
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <p className="text-sm font-medium">
                {t('testMode')}
              </p>
            </div>
          </div>

          <div className="text-center py-8">
            <div className="mb-6 flex justify-center">
              <div className="bg-primary/10 p-6 rounded-full">
                <CreditCardIcon className="text-primary" width={64} height={64} />
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-4 text-emphasis-900">
              {t('simulatePayment')}
            </h3>

            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              {t('testDescription')}
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left mb-6">
              <h4 className="font-semibold mb-3 text-blue-900">
                {t('paymentDetails')}
              </h4>
              <div className="space-y-2 text-sm">
                {renderPaymentInfo()}
                <p className="mt-3 pt-3 border-t border-blue-300"><strong>{t('code')}</strong> {props.hiringCode}</p>
                <p><strong>{t('status')}</strong> {t('simulation')}</p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={props.onBack}
              variant="outline"
              className="flex-1"
            >
              {t('common:buttons.back')}
            </Button>
            <Button
              onClick={handleTestPayment}
              className="flex-1 bg-primary hover:bg-primary-700 text-white"
            >
              <CheckCircleIcon className="mr-2" width={18} height={18} />
              {t('simulatePayment')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  } else {
    mainContent = (
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10">
          <CardTitle className="text-2xl text-emphasis-900 flex items-center gap-2">
            <CreditCardIcon className="text-primary" width={28} height={28} />
            {t('stripeTitle')}
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          <div className="text-center py-8">
            <div className="mb-6 flex justify-center">
              <div className="bg-primary/10 p-6 rounded-full">
                <CreditCardIcon className="text-primary" width={64} height={64} />
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-4 text-emphasis-900">
              {t('stripeCheckoutTitle')}
            </h3>

            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              {t('stripeDescription')}
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-left mb-6">
              <h4 className="font-semibold mb-3 text-green-900">
                {t('paymentDetails')}
              </h4>
              <div className="space-y-2 text-sm">
                {renderPaymentInfo()}
                <p className="mt-3 pt-3 border-t border-green-300"><strong>{t('code')}</strong> {props.hiringCode}</p>
                <p><strong>{t('method')}</strong> {t('stripeCheckout')}</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="text-blue-600" width={20} height={20} />
                <p className="text-sm text-blue-800">
                  <strong>{t('secureNote')}</strong> {t('secureDescription')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={props.onBack}
              variant="outline"
              className="flex-1"
            >
              {t('common:buttons.back')}
            </Button>
            <Button
              onClick={handleStripeCheckout}
              className="flex-1 bg-primary hover:bg-primary-700 text-white"
              disabled={!checkoutUrl}
            >
              <CreditCardIcon className="mr-2" width={18} height={18} />
              {t('proceedPayment')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Si el pago fue confirmado por el admin, mostrar SOLO la info y botón para continuar
  if (adminManualPayment) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        {manualCard}
      </div>
    );
  }

  // Si no, mostrar el flujo normal (checkbox opcional + Stripe o pago manual del cliente)
  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {manualCard}
      {mainContent}
    </div>
  );
}